// Return page after paying. Asks the server, which checks with Stripe and grants the game if paid.
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { motion } from "motion/react";
import { api, ApiError } from "../api.ts";
import { useAuth } from "../auth.tsx";
import { fmtExpiry, fmtMoney } from "../format.ts";
import { Button, Icon, Spinner, useToast } from "../ui/index.tsx";
import { LogoMark } from "../ui/logo.tsx";
import { Burst, EASE } from "./member/kit.tsx";
import type { CheckoutStatus } from "../../shared/types.ts";

/** The gift key, big and copyable, with how to pass it on. */
function GiftKey({ value }: { value: string }) {
  const toast = useToast();
  const copy = async () => { await navigator.clipboard.writeText(value); toast("Key copied", "success"); };
  return (
    <div className="mt-6 text-left">
      <div className="kicker mb-2">// gift key</div>
      <button type="button" onClick={() => void copy()} title="Copy"
        className="group flex w-full items-center justify-between gap-3 rounded-xl border border-ok/30 bg-ok/5 px-4 py-3 font-mono text-sm tracking-wide text-fg transition-colors hover:border-ok/60">
        <span className="break-all">{value}</span><Icon name="copy" className="size-4 shrink-0 text-ok" />
      </button>
      <p className="mt-2 text-xs text-muted">Send this to whoever it's for. They sign up (or sign in) at exoware.app and enter it on their Licenses page. It works once. You can find it again any time under Licenses → Gifts.</p>
    </div>
  );
}

export default function CheckoutComplete() {
  const [params] = useSearchParams();
  const id = params.get("session_id") ?? "";
  const { refresh } = useAuth();
  const [status, setStatus] = useState<CheckoutStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    let tries = 0;
    const poll = async () => {
      try {
        const s = await api.get<CheckoutStatus>(`/checkout/session/${encodeURIComponent(id)}`);
        if (!live) return;
        setStatus(s);
        if (s.paid) { void refresh(); return; }
        // Bank redirects can take a few seconds to settle.
        if (s.status === "open" && tries++ < 15) setTimeout(poll, 2000);
      } catch (e) { if (live) setError(e instanceof ApiError ? e.message : String(e)); }
    };
    void poll();
    return () => { live = false; };
  }, [id]);

  const paid = status?.paid;
  return (
    <div className="relative grid min-h-screen place-items-center overflow-hidden px-6">
      <div className="grid-floor pointer-events-none absolute inset-x-0 top-0 h-[60vh]" />
      <div className={`pointer-events-none absolute left-1/2 top-1/3 size-[40rem] -translate-x-1/2 rounded-full blur-[150px] ${paid ? "bg-ok/15" : "bg-accent/10"}`} />
      {paid && <Burst id={1} count={48} />}
      <motion.div initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.6, ease: EASE }}
        className="pane hud hud-accent relative w-full max-w-md p-8 text-center">
        {error ? (
          <>
            <Icon name="alert" className="mx-auto mb-4 size-10 text-bad" />
            <h1 className="text-2xl font-semibold">We couldn't find that order</h1>
            <p className="mt-2 text-sm text-muted">{error}</p>
          </>
        ) : !status || (status.status === "open" && !paid) ? (
          <>
            <Spinner className="mx-auto mb-5 size-8 text-accent" />
            <h1 className="text-2xl font-semibold">Confirming your payment…</h1>
            <p className="mt-2 text-sm text-muted">This only takes a moment. Don't close the page.</p>
          </>
        ) : paid ? (
          <>
            <motion.div initial={{ scale: 0.4, rotate: -20 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 260, damping: 16 }}
              className="mx-auto mb-5 w-fit"><LogoMark className="size-20 drop-shadow-[0_0_40px_rgb(46_229_138/0.45)]" /></motion.div>
            <div className="kicker mb-2 text-ok">{status.gift_key ? "// gift ready" : "// access granted"}</div>
            <h1 className="text-3xl font-bold uppercase leading-tight">{status.game_name}</h1>
            <p className="mt-2 text-muted">{status.tier_label} · {fmtMoney(status.amount_cents, status.currency)}</p>
            {status.gift_key && <GiftKey value={status.gift_key} />}
            {status.entitlement && (
              <div className="mx-auto mt-5 w-fit rounded-xl border border-ok/30 bg-ok/10 px-4 py-2 text-sm text-ok">
                {status.entitlement.expires_at === null ? "Lifetime access" : fmtExpiry(status.entitlement.expires_at)}
              </div>
            )}
            <div className="mt-8 grid gap-2 sm:grid-cols-2">
              {status.gift_key
                ? <Link to="/pricing"><Button variant="primary" className="w-full" icon="gift">Buy another</Button></Link>
                : <Link to="/downloads"><Button variant="primary" className="w-full" icon="download">Get the loader</Button></Link>}
              <Link to="/licenses"><Button className="w-full">View licenses</Button></Link>
            </div>
          </>
        ) : (
          <>
            <Icon name="x" className="mx-auto mb-4 size-10 text-muted" />
            <h1 className="text-2xl font-semibold">Payment not completed</h1>
            <p className="mt-2 text-sm text-muted">You weren't charged. You can try again whenever you like.</p>
            <Link to={status.kind === "bundle" ? "/pricing#bundles" : `/checkout/${status.game_id}/0`}><Button variant="primary" className="mt-6">Try again</Button></Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
